import React, { useState, useEffect } from 'react';
import {
  HomeIcon,
  MagnifyingGlassIcon,
  SpeakerWaveIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  BuildingStorefrontIcon,
  DocumentTextIcon,
  SparklesIcon,
  KeyIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

const sections = [
  { name: 'SuperGenie Dashboard', icon: HomeIcon },
  { name: 'Lead Generation', icon: MagnifyingGlassIcon },
  { name: 'Outreach Automation', icon: SpeakerWaveIcon },
  { name: 'CRM & Pipeline', icon: BuildingOfficeIcon },
  { name: 'Visual Workflow Builder', icon: BoltIcon },
  { name: 'Appointments', icon: CalendarDaysIcon },
  { name: 'API Keys & Integrations', icon: KeyIcon },
  { name: 'Resources & Docs', icon: DocumentTextIcon },
  { name: 'White-Label SaaS', icon: BuildingStorefrontIcon }
];

export default function Sidebar({ activeSection, onSelect, isDarkMode, toggleDarkMode, onLogout }) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleSidebar = () => {
    const nextCollapsed = !isCollapsed;
    setIsCollapsed(nextCollapsed);
    // Update CSS custom property for main content margin
    document.documentElement.style.setProperty('--sidebar-width', nextCollapsed ? '64px' : '208px');
  };

  // Set initial width
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', isCollapsed ? '64px' : '208px');
  }, [isCollapsed]);

  const handleSectionSelect = (sectionName) => {
    onSelect(sectionName);
  };

  return (
    <>
      {/* Fixed Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-40 h-screen
        ${isCollapsed ? 'w-16' : 'w-52'}
        bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 
        border-r border-gray-700 
        shadow-xl
        transition-all duration-300 ease-in-out
        flex flex-col
      `}>
        {/* Sidebar Header with Hamburger */}
        <div className="p-3 border-b border-gray-700 flex items-center justify-between">
          {!isCollapsed && (
            <div>
              <h2 className="text-lg font-semibold text-white">Market Genie</h2>
              <p className="text-gray-400 text-xs mt-1">AI Marketing Platform</p>
            </div>
          )}
          
          {/* Hamburger Button */}
          <button
            onClick={toggleSidebar}
            className="text-white hover:text-teal-400 transition-colors duration-200 p-2 rounded-lg hover:bg-gray-800"
            aria-label="Toggle sidebar"
          >
            {isCollapsed ? (
              <Bars3Icon className="w-6 h-6" />
            ) : (
              <XMarkIcon className="w-6 h-6" />
            )}
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="p-3 flex-1 overflow-y-auto">
          <ul className="space-y-1.5">
            {sections.map(section => {
              const Icon = section.icon;
              return (
                <li key={section.name}>
                  <button
                    onClick={() => handleSectionSelect(section.name)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-all duration-200 hover:bg-gray-800 hover:shadow-md group ${
                      activeSection === section.name
                        ? 'bg-gradient-to-r from-teal-600 to-teal-500 text-white shadow-lg'
                        : 'text-gray-300 hover:text-white'
                    }`}
                    title={isCollapsed ? section.name : ''}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${activeSection === section.name ? 'text-white' : 'text-gray-400 group-hover:text-teal-400'}`} />
                    {!isCollapsed && (
                      <span className={`text-sm font-medium ${activeSection === section.name ? 'text-white' : 'group-hover:text-white'}`}>
                        {section.name}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Controls */}
        <div className="p-3 border-t border-gray-700 mt-auto">
          <div className="space-y-2">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-all duration-200 hover:bg-gray-800 text-gray-300 hover:text-white group"
              title={isCollapsed ? (isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode') : ''}
            >
              <span className="w-4 h-4 flex-shrink-0 text-base group-hover:text-teal-400">
                {isDarkMode ? '☀️' : '🌙'}
              </span>
              {!isCollapsed && (
                <span className="text-sm font-medium group-hover:text-white">
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </span>
              )}
            </button>

            {/* Logout */}
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-all duration-200 hover:bg-red-800 text-gray-300 hover:text-white group"
              title={isCollapsed ? 'Logout' : ''}
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-red-400" />
              {!isCollapsed && (
                <span className="text-sm font-medium group-hover:text-white">Logout</span>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
